const preventDefault = (event) => {
  event.preventDefault();
  return false;
};

class CustomSlider extends HTMLElement {
  constructor() {
    super();

    this.currentPosition = 0;
    this.isDragging = false;
    this.startX = 0;
    this.startPosition = 0;
    this.totalMovement = 0; // Track total movement distance
    this.currentIndex = 0;
    this.isTransitioning = false;
    this.transitionDuration = 300; // Default transition duration in ms
    this.prevArrow = null;
    this.nextArrow = null;

    this.handleDrag = this.handleDrag.bind(this);
    this.startDrag = this.startDrag.bind(this);
    this.endDrag = this.endDrag.bind(this);
    this.handleLinkClick = this.handleLinkClick.bind(this);
    this.calculateBounds = this.calculateBounds.bind(this);
    this.updateBullets = this.updateBullets.bind(this);
    this.debouncedBounds = debounce(this.calculateBounds, 100);
  }

  connectedCallback() {
    if (this.initialized) return;

    this.track =
      this.querySelector("[data-slider-track]") || this.firstElementChild;
    if (!this.track) return;

    this.slides = Array.from(this.track.querySelectorAll("[data-slider-item]"));
    // Allow bullets even with 1 slide, but slider functionality needs 2+
    const needsSlides = this.slides.length < 2;

    this.clickThreshold = parseInt(
      this.getAttribute("click-threshold") || "5",
      10
    );
    this.dragThreshold = parseInt(
      this.getAttribute("drag-threshold") || "30",
      10
    );

    // Get transition duration from attribute or use default
    this.transitionDuration = parseInt(
      this.getAttribute("transition-duration") || "300",
      10
    );

    // Bullets configuration
    const bulletsAttr = this.getAttribute("data-bullets");
    this.bulletsEnabled = bulletsAttr !== null && bulletsAttr !== "";
    this.bulletsId = bulletsAttr || null;
    this.bulletsContainer = null;
    this.bullets = [];

    // Infinite mode configuration
    this.infiniteEnabled = this.getAttribute("data-enable-infinite") !== null;
    this.clonedSlides = [];
    this.originalSlidesCount = this.slides.length;

    this.querySelectorAll("img").forEach((img) => {
      img.setAttribute("draggable", "false");
      img.addEventListener("dragstart", preventDefault, { passive: false });
    });

    // Initialize infinite mode if enabled
    if (this.infiniteEnabled && this.slides.length >= 2) {
      this.initInfinite();
    }

    // Initialize bullets even if there's only 1 slide
    this.initBullets();

    // Initialize arrow navigation if arrows exist
    this.initArrows();

    // Only initialize slider functionality if there are 2+ slides
    if (needsSlides) {
      this.initialized = true;
      // Update arrows state on initial load
      this.updateArrows();
      return;
    }

    this.calculateBounds();

    window.addEventListener("resize", this.debouncedBounds);
    window.addEventListener("load", this.calculateBounds);

    this.track.addEventListener("mousedown", this.startDrag);
    this.track.addEventListener("touchstart", this.startDrag, {
      passive: false,
    });

    // Prevent link clicks during drag operations (use capture phase)
    this.track.addEventListener("click", this.handleLinkClick, true);

    this.initialized = true;
  }

  disconnectedCallback() {
    if (!this.initialized) return;

    window.removeEventListener("resize", this.debouncedBounds);
    window.removeEventListener("load", this.calculateBounds);

    this.track.removeEventListener("mousedown", this.startDrag);
    this.track.removeEventListener("touchstart", this.startDrag);
    this.track.removeEventListener("click", this.handleLinkClick, true);

    document.removeEventListener("mousemove", this.handleDrag);
    document.removeEventListener("touchmove", this.handleDrag);
    document.removeEventListener("mouseup", this.endDrag);
    document.removeEventListener("touchend", this.endDrag);

    this.initialized = false;
  }

  handleLinkClick(event) {
    // If we're dragging or movement exceeded threshold, prevent link click
    if (this.isDragging || (this.totalMovement || 0) >= this.clickThreshold) {
      event.preventDefault();
      event.stopPropagation();
      return false;
    }
  }

  startDrag(event) {
    if (event.type === "mousedown" && event.button !== 0) return;
    if (this.isTransitioning) {
      event.preventDefault();
      return;
    }

    this.isDragging = false;
    this.totalMovement = 0; // Reset movement tracking
    this.startX =
      event.type === "touchstart" ? event.touches[0].clientX : event.clientX;
    this.startPosition = this.currentPosition;

    document.addEventListener("mousemove", this.handleDrag);
    document.addEventListener("touchmove", this.handleDrag, { passive: false });
    document.addEventListener("mouseup", this.endDrag);
    document.addEventListener("touchend", this.endDrag);

    event.preventDefault();
  }

  handleDrag(event) {
    const currentX =
      event.type === "touchmove" ? event.touches[0].clientX : event.clientX;
    const diff = this.startX - currentX;

    // Track total movement distance (always update, even if below threshold)
    this.totalMovement = Math.abs(diff);

    // If movement is below threshold and we haven't started dragging, don't proceed
    if (!this.isDragging && Math.abs(diff) < this.clickThreshold) {
      return;
    }

    if (!this.isDragging) {
      this.isDragging = true;
      this.track.classList.add("dragging");
      document.body.classList.add("slider-dragging");
      this.track.style.transition = "none";

      // Prevent all link clicks during drag
      const links = this.track.querySelectorAll("a");
      links.forEach((link) => {
        link.style.pointerEvents = "none";
      });
    }

    let nextPosition = this.startPosition - diff;

    if (this.infiniteEnabled) {
      // In infinite mode, allow going beyond bounds temporarily
      const minPosition = -(
        (this.originalSlidesCount + 2) *
        (this.cardWidth + this.gap)
      );
      const maxPosition = this.cardWidth + this.gap;
      nextPosition = Math.max(Math.min(nextPosition, maxPosition), minPosition);
    } else {
      nextPosition = Math.max(Math.min(nextPosition, 0), -this.maxPosition);
    }

    this.currentPosition = nextPosition;
    this.track.style.transform = `translateX(${nextPosition}px)`;

    event.preventDefault();
  }

  endDrag(event) {
    document.removeEventListener("mousemove", this.handleDrag);
    document.removeEventListener("touchmove", this.handleDrag);
    document.removeEventListener("mouseup", this.endDrag);
    document.removeEventListener("touchend", this.endDrag);

    document.body.classList.remove("slider-dragging");

    // Re-enable link pointer events
    const links = this.track.querySelectorAll("a");
    links.forEach((link) => {
      link.style.pointerEvents = "";
    });

    // Only allow link click if there was no dragging AND movement was below threshold
    // totalMovement will be 0 if handleDrag was never called (pure click)
    if (!this.isDragging && (this.totalMovement || 0) < this.clickThreshold) {
      const target = event.target.closest("a");
      if (target) {
        // Prevent default to avoid double-firing, then programmatically click
        event.preventDefault();
        event.stopPropagation();
        // Small delay to ensure drag handlers are cleaned up
        setTimeout(() => {
          target.click();
        }, 10);
      }
      this.isDragging = false;
      this.track.classList.remove("dragging");
      return;
    }

    this.isDragging = false;
    this.track.classList.remove("dragging");

    const movement = this.currentPosition - this.startPosition;

    if (this.cardWidth > 0) {
      if (movement <= -this.dragThreshold) {
        // Moving to next slide
        if (this.infiniteEnabled) {
          this.goToNext();
          return;
        } else {
          this.currentPosition = Math.max(
            this.startPosition - this.cardWidth,
            -this.maxPosition
          );
        }
      } else if (movement >= this.dragThreshold) {
        // Moving to previous slide
        if (this.infiniteEnabled) {
          this.goToPrev();
          return;
        } else {
          this.currentPosition = Math.min(
            this.startPosition + this.cardWidth,
            0
          );
        }
      } else {
        this.currentPosition = this.startPosition;
      }
    } else {
      if (this.infiniteEnabled) {
        // In infinite mode, allow going beyond bounds temporarily
        // Clamp to reasonable bounds
        const minPosition = -(
          (this.originalSlidesCount + 2) *
          (this.cardWidth + this.gap)
        );
        const maxPosition = this.cardWidth + this.gap;
        this.currentPosition = Math.max(
          Math.min(this.currentPosition, maxPosition),
          minPosition
        );
      } else {
        this.currentPosition = Math.max(
          Math.min(this.currentPosition, 0),
          -this.maxPosition
        );
      }
    }

    this.slideTo(this.currentPosition);
    this.updateCurrentIndex();
    this.updateBullets();
    this.updateArrows();
  }

  slideTo(position) {
    if (this.isTransitioning) return;

    this.currentPosition = position;
    this.track.style.transition = `transform ${this.transitionDuration}ms ease`;
    this.track.style.transform = `translateX(${position}px)`;

    // Set transitioning state
    this.isTransitioning = true;
    this.disableInteractions();

    // Clear transitioning state after animation completes
    setTimeout(() => {
      this.isTransitioning = false;
      this.enableInteractions();
      this.updateCurrentIndex();
      this.updateBullets();
      this.updateArrows();
    }, this.transitionDuration);
  }

  goToSlide(index) {
    if (this.isTransitioning) return;

    if (!this.infiniteEnabled) {
      // Normal mode: clamp index
      if (index < 0 || index >= this.originalSlidesCount) return;
      const cardWidth = this.cardWidth || this.slides[0].offsetWidth;
      const gap = this.gap || 0;
      const position = -(index * (cardWidth + gap));
      this.slideTo(position);
      return;
    }

    // Infinite mode: handle wrapping
    // Real slides are at positions 1 to originalSlidesCount (accounting for clone at 0)
    // Clone of last slide is at position 0
    // Clone of first slide is at position originalSlidesCount + 1

    const cardWidth = this.cardWidth || this.slides[0].offsetWidth;
    const gap = this.gap || 0;

    // Normalize index to be within real slides range
    let realIndex = index;
    if (realIndex < 0) {
      realIndex = this.originalSlidesCount - 1;
    } else if (realIndex >= this.originalSlidesCount) {
      realIndex = 0;
    }

    // Calculate position: realIndex + 1 (because position 0 is the last clone)
    const targetPosition = -((realIndex + 1) * (cardWidth + gap));
    this.slideTo(targetPosition);
  }

  goToNext() {
    if (this.isTransitioning) return;

    if (!this.infiniteEnabled) {
      const nextIndex = Math.min(
        this.currentIndex + 1,
        this.originalSlidesCount - 1
      );
      this.goToSlide(nextIndex);
      return;
    }

    // Infinite mode: always allow going to next
    const nextIndex = (this.currentIndex + 1) % this.originalSlidesCount;
    const cardWidth = this.cardWidth || this.slides[0].offsetWidth;
    const gap = this.gap || 0;

    // If going from last to first, jump to first clone then reset
    if (this.currentIndex === this.originalSlidesCount - 1) {
      const firstClonePosition = -(
        (this.originalSlidesCount + 1) *
        (cardWidth + gap)
      );
      this.slideTo(firstClonePosition);
      setTimeout(() => {
        this.resetToRealSlide(0);
      }, this.transitionDuration);
    } else {
      this.goToSlide(nextIndex);
    }
  }

  goToPrev() {
    if (this.isTransitioning) return;

    if (!this.infiniteEnabled) {
      const prevIndex = Math.max(this.currentIndex - 1, 0);
      this.goToSlide(prevIndex);
      return;
    }

    // Infinite mode: always allow going to previous
    const prevIndex =
      (this.currentIndex - 1 + this.originalSlidesCount) %
      this.originalSlidesCount;
    const cardWidth = this.cardWidth || this.slides[0].offsetWidth;
    const gap = this.gap || 0;

    // If going from first to last, jump to last clone then reset
    if (this.currentIndex === 0) {
      const lastClonePosition = 0;
      this.slideTo(lastClonePosition);
      setTimeout(() => {
        this.resetToRealSlide(this.originalSlidesCount - 1);
      }, this.transitionDuration);
    } else {
      this.goToSlide(prevIndex);
    }
  }

  initInfinite() {
    if (this.slides.length < 2) return;

    // Clone first slide and append to end
    const firstClone = this.slides[0].cloneNode(true);
    firstClone.setAttribute("data-slider-clone", "last");
    this.track.appendChild(firstClone);
    this.clonedSlides.push(firstClone);

    // Clone last slide and prepend to beginning
    const lastClone = this.slides[this.originalSlidesCount - 1].cloneNode(true);
    lastClone.setAttribute("data-slider-clone", "first");
    this.track.insertBefore(lastClone, this.track.firstChild);
    this.clonedSlides.push(lastClone);

    // Update slides array to include clones
    this.slides = Array.from(this.track.querySelectorAll("[data-slider-item]"));

    // Set initial position to first real slide (index 1, since index 0 is the last clone)
    const cardWidth = this.slides[0].offsetWidth;
    const gap = parseFloat(window.getComputedStyle(this.track).gap) || 0;
    this.currentPosition = -(1 * (cardWidth + gap));
    this.track.style.transition = "none";
    this.track.style.transform = `translateX(${this.currentPosition}px)`;

    // Re-enable transition after initial positioning
    requestAnimationFrame(() => {
      this.track.style.transition = "";
    });
  }

  resetToRealSlide(realIndex) {
    // Reset position without transition to create seamless loop
    if (!this.infiniteEnabled) return;

    const cardWidth = this.cardWidth || this.slides[0].offsetWidth;
    const gap = this.gap || 0;

    // Calculate position: realIndex + 1 (because position 0 is the last clone)
    const targetPosition = -((realIndex + 1) * (cardWidth + gap));

    // Disable transition for instant reset
    this.track.style.transition = "none";
    this.currentPosition = targetPosition;
    this.track.style.transform = `translateX(${targetPosition}px)`;

    // Force reflow
    void this.track.offsetHeight;

    // Re-enable transition after reset and clear transitioning state
    requestAnimationFrame(() => {
      this.track.style.transition = `transform ${this.transitionDuration}ms ease`;
      this.isTransitioning = false;
      this.enableInteractions();
      this.updateCurrentIndex();
      this.updateBullets();
      this.updateArrows();
    });
  }

  initBullets() {
    if (!this.bulletsEnabled || !this.bulletsId) return;

    // Try to find existing bullets container by ID
    this.bulletsContainer = document.querySelector(
      `[data-bullets-id="${this.bulletsId}"]`
    );

    if (!this.bulletsContainer) {
      // Create bullets container if it doesn't exist
      this.bulletsContainer = document.createElement("div");
      this.bulletsContainer.className = "slider-bullets";
      this.bulletsContainer.setAttribute("data-bullets-id", this.bulletsId);
      this.bulletsContainer.setAttribute("data-bullets-container", "");

      // Try to find a parent container with position relative (for proper positioning)
      // Priority: banner-container, slider-container, or any positioned parent
      let parentContainer = this.closest(".banner-container");

      if (!parentContainer) {
        // Try other common container names
        parentContainer =
          this.closest(".slider-container") ||
          this.closest("[class*='container']");
      }

      if (!parentContainer) {
        // Check parent's computed style for positioning
        let parent = this.parentElement;
        while (parent && parent !== document.body) {
          const style = window.getComputedStyle(parent);
          if (style.position !== "static") {
            parentContainer = parent;
            break;
          }
          parent = parent.parentElement;
        }
      }

      // Append to the found container or slider's parent, otherwise to slider itself
      if (parentContainer) {
        parentContainer.appendChild(this.bulletsContainer);
      } else {
        // Fallback: append to the slider element (will be positioned by CSS)
        this.appendChild(this.bulletsContainer);
      }

      // Create bullets - use original slides count (not including clones)
      const slidesCount = this.infiniteEnabled
        ? this.originalSlidesCount
        : this.slides.length;
      for (let i = 0; i < slidesCount; i++) {
        const bullet = document.createElement("button");
        bullet.className = "slider-bullet";
        bullet.setAttribute("aria-label", `Go to slide ${i + 1}`);
        bullet.setAttribute("data-slide-index", i);
        bullet.addEventListener("click", () => {
          if (!this.isTransitioning) {
            this.goToSlide(i);
          }
        });
        this.bulletsContainer.appendChild(bullet);
        this.bullets.push(bullet);
      }
    } else {
      // Use existing bullets container
      this.bullets = Array.from(
        this.bulletsContainer.querySelectorAll(
          ".slider-bullet, [data-slide-index]"
        )
      );
      this.bullets.forEach((bullet, index) => {
        const slideIndex = parseInt(
          bullet.getAttribute("data-slide-index") || index,
          10
        );
        bullet.addEventListener("click", () => {
          if (!this.isTransitioning) {
            this.goToSlide(slideIndex);
          }
        });
      });
    }

    // Add initial active state
    if (this.bullets.length > 0) {
      this.updateBullets();
    }
  }

  disableInteractions() {
    // Disable bullets
    if (this.bulletsContainer) {
      this.bulletsContainer.style.pointerEvents = "none";
      this.bulletsContainer.style.opacity = "0.5";
    }

    // Disable track dragging
    if (this.track) {
      this.track.style.pointerEvents = "none";
    }

    // Disable arrow buttons
    if (this.prevArrow) {
      this.prevArrow.style.pointerEvents = "none";
      this.prevArrow.style.opacity = "0.5";
      this.prevArrow.disabled = true;
    }
    if (this.nextArrow) {
      this.nextArrow.style.pointerEvents = "none";
      this.nextArrow.style.opacity = "0.5";
      this.nextArrow.disabled = true;
    }

    // Add class to slider for external styling
    this.classList.add("slider-transitioning");
  }

  enableInteractions() {
    // Enable bullets
    if (this.bulletsContainer) {
      this.bulletsContainer.style.pointerEvents = "";
      this.bulletsContainer.style.opacity = "";
    }

    // Enable track dragging
    if (this.track) {
      this.track.style.pointerEvents = "";
    }

    // Enable arrow buttons
    if (this.prevArrow) {
      this.prevArrow.style.pointerEvents = "";
      this.prevArrow.style.opacity = "";
      this.prevArrow.disabled = false;
    }
    if (this.nextArrow) {
      this.nextArrow.style.pointerEvents = "";
      this.nextArrow.style.opacity = "";
      this.nextArrow.disabled = false;
    }

    // Remove class from slider
    this.classList.remove("slider-transitioning");
  }

  initArrows() {
    // Find arrow buttons by common data attributes or classes
    const parent = this.parentElement;
    if (!parent) return;

    this.prevArrow = parent.querySelector(
      "[data-banner-prev], [data-slider-prev], .banner-arrow--prev, .slider-arrow--prev"
    );
    this.nextArrow = parent.querySelector(
      "[data-banner-next], [data-slider-next], .banner-arrow--next, .slider-arrow--next"
    );

    if (this.prevArrow) {
      this.prevArrow.addEventListener("click", () => {
        if (!this.isTransitioning && this.goToPrev) {
          this.goToPrev();
        }
      });
    }

    if (this.nextArrow) {
      this.nextArrow.addEventListener("click", () => {
        if (!this.isTransitioning && this.goToNext) {
          this.goToNext();
        }
      });
    }
  }

  updateArrows() {
    if (!this.prevArrow || !this.nextArrow) return;

    // In infinite mode, arrows are always enabled
    if (this.infiniteEnabled) {
      this.prevArrow.disabled = false;
      this.nextArrow.disabled = false;
      return;
    }

    // Disable prev arrow at first slide (index 0)
    this.prevArrow.disabled = this.currentIndex === 0;

    // Disable next arrow at last slide
    this.nextArrow.disabled = this.currentIndex >= this.originalSlidesCount - 1;
  }

  updateCurrentIndex() {
    if (!this.cardWidth || this.slides.length === 0) return;

    const position = Math.abs(this.currentPosition);
    const cardWidth = this.cardWidth;
    const gap = this.gap || 0;
    let index = Math.round(position / (cardWidth + gap));

    if (this.infiniteEnabled && this.originalSlidesCount > 0) {
      // In infinite mode, index 0 is last clone, indices 1 to originalSlidesCount are real slides
      // Index originalSlidesCount + 1 is first clone
      if (index === 0) {
        // At last clone, show as last real slide
        this.currentIndex = this.originalSlidesCount - 1;
      } else if (index === this.originalSlidesCount + 1) {
        // At first clone, show as first real slide
        this.currentIndex = 0;
      } else if (index >= 1 && index <= this.originalSlidesCount) {
        // Real slide (adjust for clone offset)
        this.currentIndex = index - 1;
      } else {
        this.currentIndex = Math.min(
          Math.max(index - 1, 0),
          this.originalSlidesCount - 1
        );
      }
    } else {
      this.currentIndex = Math.min(Math.max(index, 0), this.slides.length - 1);
    }
  }

  updateBullets() {
    if (!this.bulletsEnabled || this.bullets.length === 0) return;

    // In infinite mode, use original slides count for bullets
    const slidesCount = this.infiniteEnabled
      ? this.originalSlidesCount
      : this.slides.length;
    const realIndex = this.infiniteEnabled
      ? this.currentIndex
      : this.currentIndex;

    this.bullets.forEach((bullet, index) => {
      const slideIndex = parseInt(
        bullet.getAttribute("data-slide-index") || index,
        10
      );
      // Ensure slideIndex is within bounds
      const normalizedIndex =
        slideIndex >= slidesCount ? slideIndex % slidesCount : slideIndex;
      bullet.classList.toggle("active", normalizedIndex === realIndex);
    });
  }

  calculateBounds() {
    const containerWidth = this.getBoundingClientRect().width;
    const trackStyles = window.getComputedStyle(this.track);

    const gapValue = parseFloat(
      trackStyles.columnGap || trackStyles.gap || "0"
    );
    const paddingLeft = parseFloat(trackStyles.paddingLeft || "0");
    const paddingRight = parseFloat(trackStyles.paddingRight || "0");

    this.gap = isNaN(gapValue) ? 0 : gapValue;

    const firstSlideRect = this.slides[0].getBoundingClientRect();
    this.cardWidth = firstSlideRect.width + this.gap;

    const trackWidth =
      this.cardWidth * this.slides.length + paddingLeft + paddingRight;
    this.maxPosition = Math.max(trackWidth - containerWidth, 0);

    if (this.infiniteEnabled) {
      // In infinite mode, don't clamp position (allow going to clones)
      // But ensure we're within reasonable bounds
      const minPosition = -(
        (this.originalSlidesCount + 2) *
        (this.cardWidth + this.gap)
      );
      const maxPosition = this.cardWidth + this.gap;
      this.currentPosition = Math.max(
        Math.min(this.currentPosition, maxPosition),
        minPosition
      );
    } else {
      this.currentPosition = Math.max(
        Math.min(this.currentPosition, 0),
        -this.maxPosition
      );
    }

    const previousTransition = this.track.style.transition;
    this.track.style.transition = "none";
    this.track.style.transform = `translateX(${this.currentPosition}px)`;
    requestAnimationFrame(() => {
      this.track.style.transition =
        previousTransition || `transform ${this.transitionDuration}ms ease-out`;
      this.updateCurrentIndex();
      this.updateBullets();
      this.updateArrows();
    });
  }
}

if (!customElements.get("custom-slider")) {
  customElements.define("custom-slider", CustomSlider);
}

// ------------------CUSTOM FAQ (Web Component)-------------------------
class CustomFaq extends HTMLElement {
  constructor() {
    super();

    this.TRANSITION_DURATION = 250;
    this.isAnimating = false;
    this.sectionId = this.getAttribute("data-section-id") || "";
    this.allowMultiple = this.getAttribute("data-allow-multiple") === "true";

    this.handleAccordion = this.handleAccordion.bind(this);
  }

  connectedCallback() {
    if (this.initialized) return;

    // Find all accordion items within this component
    this.items = Array.from(
      this.querySelectorAll(".accordion_item-basic, .faq-basic")
    );
    if (this.items.length === 0) return;

    // Find all buttons and collapses
    this.buttons = Array.from(this.querySelectorAll("[data-accordion-toggle]"));
    this.collapses = Array.from(this.querySelectorAll(".accordion-collapse"));

    if (this.buttons.length === 0 || this.collapses.length === 0) return;

    // Check for initial active index
    const initialActiveIndex = this.getAttribute("data-initial-active");
    let initialActiveButton = null;
    let initialActiveCollapse = null;

    if (initialActiveIndex !== null) {
      const index = parseInt(initialActiveIndex, 10);
      if (!isNaN(index) && index >= 0 && index < this.buttons.length) {
        initialActiveButton = this.buttons[index];
        const targetId = initialActiveButton.getAttribute("aria-controls");
        initialActiveCollapse = document.getElementById(targetId);
      }
    }

    // Initialize all accordions as closed
    this.collapses.forEach((collapse) => {
      collapse.style.height = "0";
    });

    // If initial active is specified, open it
    if (initialActiveButton && initialActiveCollapse) {
      initialActiveButton.setAttribute("aria-expanded", "true");
      initialActiveCollapse.classList.add("show");
      const item =
        initialActiveButton.closest(".accordion_item-basic") ||
        initialActiveButton.closest(".faq-basic");
      if (item) item.classList.add("active");

      // Set height to auto after a brief delay to allow for smooth initial render
      requestAnimationFrame(() => {
        // Wait for images to load before calculating heights
        const answerEl = initialActiveButton?.querySelector(".faq-answer");
        const imageCol = initialActiveButton?.querySelector(".faq-col-image");

        const updateInitialHeights = () => {
          if (answerEl) {
            answerEl.style.maxHeight = "none";
            const answerHeight = answerEl.scrollHeight;
            answerEl.style.maxHeight = `${answerHeight}px`;
          }

          if (imageCol) {
            const img = imageCol.querySelector("img");
            if (img) {
              const setImageHeight = () => {
                imageCol.style.maxHeight = "none";
                const imageHeight = imageCol.scrollHeight;
                imageCol.style.maxHeight = `${imageHeight}px`;

                // Also update collapse height if image changed the total height
                if (initialActiveCollapse) {
                  const newContentHeight = initialActiveCollapse.scrollHeight;
                  initialActiveCollapse.style.maxHeight = `${newContentHeight}px`;
                }
              };

              if (img.complete) {
                // Image already loaded
                setImageHeight();
              } else {
                // Wait for image to load
                img.addEventListener("load", setImageHeight, { once: true });
                img.addEventListener("error", setImageHeight, { once: true }); // Handle load errors
              }
            } else {
              // No image, just set height
              imageCol.style.maxHeight = "none";
              const imageHeight = imageCol.scrollHeight;
              imageCol.style.maxHeight = `${imageHeight}px`;
            }
          }

          const contentHeight = initialActiveCollapse.scrollHeight;
          initialActiveCollapse.style.height = `${contentHeight}px`;
          initialActiveCollapse.style.maxHeight = `${contentHeight}px`;

          setTimeout(() => {
            initialActiveCollapse.style.height = "auto";
            initialActiveCollapse.style.maxHeight = `${initialActiveCollapse.scrollHeight}px`;

            // Recalculate after transition in case image loaded during animation
            if (answerEl) {
              answerEl.style.maxHeight = `${answerEl.scrollHeight}px`;
            }
            if (imageCol) {
              imageCol.style.maxHeight = `${imageCol.scrollHeight}px`;
            }
          }, this.TRANSITION_DURATION);
        };

        updateInitialHeights();
      });
    }

    // Add event listeners to all buttons
    this.buttons.forEach((button) => {
      button.addEventListener("click", this.handleAccordion);
    });

    this.initialized = true;
  }

  disconnectedCallback() {
    if (!this.initialized) return;

    // Remove event listeners
    this.buttons.forEach((button) => {
      button.removeEventListener("click", this.handleAccordion);
    });

    this.initialized = false;
  }

  handleAccordion(evt) {
    // Prevent rapid clicks during animation
    if (this.isAnimating) return;

    const button = evt.currentTarget;
    const isExpanded = button.getAttribute("aria-expanded") === "true";
    const targetId = button.getAttribute("aria-controls");
    const targetCollapse = document.getElementById(targetId);

    if (!targetCollapse || targetCollapse.classList.contains("collapsing"))
      return;

    // Set animation state
    this.isAnimating = true;

    // Close all other accordions if not allowing multiple
    if (!this.allowMultiple) {
      this.buttons.forEach((btn) => {
        if (btn !== button) {
          const otherTargetId = btn.getAttribute("aria-controls");
          const otherCollapse = document.getElementById(otherTargetId);

          if (otherCollapse && otherCollapse.classList.contains("show")) {
            btn.setAttribute("aria-expanded", "false");
            otherCollapse.classList.add("collapsing");
            // Don't remove 'show' class yet - keep it during animation

            // Reset answer and image heights for closing animation
            const otherAnswerEl = btn.querySelector(".faq-answer");
            const otherImageCol = btn.querySelector(".faq-col-image");

            if (otherAnswerEl) {
              otherAnswerEl.style.maxHeight = `${otherAnswerEl.scrollHeight}px`;
              void otherAnswerEl.offsetHeight;
              otherAnswerEl.style.maxHeight = "0";
            }

            if (otherImageCol) {
              otherImageCol.style.maxHeight = `${otherImageCol.scrollHeight}px`;
              void otherImageCol.offsetHeight;
              otherImageCol.style.maxHeight = "0";
            }

            otherCollapse.style.height = `${otherCollapse.scrollHeight}px`;
            otherCollapse.style.maxHeight = `${otherCollapse.scrollHeight}px`;
            void otherCollapse.offsetHeight;
            otherCollapse.style.height = "0";
            otherCollapse.style.maxHeight = "0";

            setTimeout(() => {
              otherCollapse.classList.remove("show", "collapsing");
              otherCollapse.style.height = "";
              otherCollapse.style.maxHeight = "";

              // Reset answer and image max-heights
              if (otherAnswerEl) {
                otherAnswerEl.style.maxHeight = "";
              }
              if (otherImageCol) {
                otherImageCol.style.maxHeight = "";
              }

              const item =
                btn.closest(".accordion_item-basic") ||
                btn.closest(".faq-basic");
              if (item) item.classList.remove("active");
            }, this.TRANSITION_DURATION);
          }
        }
      });
    }

    // Toggle current accordion
    if (!isExpanded) {
      button.setAttribute("aria-expanded", "true");
      targetCollapse.classList.add("collapsing", "show");
      const item =
        button.closest(".accordion_item-basic") || button.closest(".faq-basic");
      if (item) item.classList.add("active");

      // Wait for images to load before calculating height
      const updateHeights = () => {
        // Calculate heights for answer and image
        const answerEl = button.querySelector(".faq-answer");
        const imageCol = button.querySelector(".faq-col-image");

        if (answerEl) {
          // Temporarily set to auto to get real height
          answerEl.style.maxHeight = "none";
          const answerHeight = answerEl.scrollHeight;
          answerEl.style.maxHeight = `${answerHeight}px`;
        }

        if (imageCol) {
          // Wait for image to load if it exists
          const img = imageCol.querySelector("img");
          if (img) {
            const setImageHeight = () => {
              imageCol.style.maxHeight = "none";
              const imageHeight = imageCol.scrollHeight;
              imageCol.style.maxHeight = `${imageHeight}px`;

              // Also update collapse height if image changed the total height
              if (targetCollapse) {
                const newContentHeight = targetCollapse.scrollHeight;
                targetCollapse.style.maxHeight = `${newContentHeight}px`;
              }
            };

            if (img.complete) {
              // Image already loaded
              setImageHeight();
            } else {
              // Wait for image to load
              img.addEventListener("load", setImageHeight, { once: true });
              img.addEventListener("error", setImageHeight, { once: true }); // Handle load errors
            }
          } else {
            // No image, just set height
            imageCol.style.maxHeight = "none";
            const imageHeight = imageCol.scrollHeight;
            imageCol.style.maxHeight = `${imageHeight}px`;
          }
        }

        // Update collapse height
        targetCollapse.style.height = "0";
        const contentHeight = targetCollapse.scrollHeight;
        targetCollapse.style.height = `${contentHeight}px`;
        targetCollapse.style.maxHeight = `${contentHeight}px`;
      };

      // Small delay to ensure DOM is ready
      requestAnimationFrame(() => {
        updateHeights();

        setTimeout(() => {
          targetCollapse.classList.remove("collapsing");
          targetCollapse.style.height = "auto";
          targetCollapse.style.maxHeight = `${targetCollapse.scrollHeight}px`;

          // Recalculate after transition in case image loaded during animation
          const answerEl = button.querySelector(".faq-answer");
          const imageCol = button.querySelector(".faq-col-image");
          if (answerEl) {
            answerEl.style.maxHeight = `${answerEl.scrollHeight}px`;
          }
          if (imageCol) {
            imageCol.style.maxHeight = `${imageCol.scrollHeight}px`;
          }

          this.isAnimating = false;
        }, this.TRANSITION_DURATION);
      });
    } else {
      button.setAttribute("aria-expanded", "false");
      targetCollapse.classList.add("collapsing");
      // Don't remove 'show' class yet - keep it during animation

      // Reset answer and image heights for closing animation
      const answerEl = button.querySelector(".faq-answer");
      const imageCol = button.querySelector(".faq-col-image");

      if (answerEl) {
        answerEl.style.maxHeight = `${answerEl.scrollHeight}px`;
        void answerEl.offsetHeight; // Force reflow
        answerEl.style.maxHeight = "0";
      }

      if (imageCol) {
        imageCol.style.maxHeight = `${imageCol.scrollHeight}px`;
        void imageCol.offsetHeight; // Force reflow
        imageCol.style.maxHeight = "0";
      }

      // Get current height
      const currentHeight = targetCollapse.scrollHeight;
      targetCollapse.style.height = `${currentHeight}px`;
      targetCollapse.style.maxHeight = `${currentHeight}px`;

      // Force reflow to ensure height is set
      void targetCollapse.offsetHeight;

      // Now animate to 0
      targetCollapse.style.height = "0";
      targetCollapse.style.maxHeight = "0";

      setTimeout(() => {
        // Remove show class after animation completes
        targetCollapse.classList.remove("show", "collapsing");
        targetCollapse.style.height = "";
        targetCollapse.style.maxHeight = "";

        // Reset answer and image max-heights
        if (answerEl) {
          answerEl.style.maxHeight = "";
        }
        if (imageCol) {
          imageCol.style.maxHeight = "";
        }

        const item =
          button.closest(".accordion_item-basic") ||
          button.closest(".faq-basic");
        if (item) item.classList.remove("active");
        this.isAnimating = false;
      }, this.TRANSITION_DURATION);
    }
  }
}

if (!customElements.get("custom-faq")) {
  customElements.define("custom-faq", CustomFaq);
}
